import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { IconDef } from '../icons/icon';

export interface BreadcrumbItem {
  label?: string;
  routerLink?: string;
  queryParams?: Record<string, unknown>;
  icon?: string | IconDef;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {

  // Inject Router only (a root-scoped singleton). We intentionally do NOT inject
  // ActivatedRoute here: this service is providedIn 'root' but is first created
  // from within a routed component's injector (via <app-breadcrumb>). Injecting
  // the route-scoped ActivatedRoute then re-enters that still-initializing
  // injector and triggers NG0200 (circular dependency). The root ActivatedRoute
  // is available without injection via router.routerState.root.
  private readonly router = inject(Router);

  private readonly breadcrumbSubject = new BehaviorSubject<BreadcrumbItem[]>([]);
  readonly breadcrumbs$ = this.breadcrumbSubject.asObservable();

  constructor() {
    this.updateBreadcrumbs();
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.updateBreadcrumbs());
  }

  private updateBreadcrumbs(): void {
    const breadcrumbs: BreadcrumbItem[] = [];
    breadcrumbs.push({
      icon: 'icon-dashboard',
      label: '',
      routerLink: '/dashboard'
    });

    this.build(this.router.routerState.root, '', breadcrumbs);

    this.breadcrumbSubject.next(breadcrumbs);
  }

  private build(
    route: ActivatedRoute,
    url: string,
    breadcrumbs: BreadcrumbItem[]
  ): void {

    const children = route.children;
    if (!children || children.length === 0) return;

    for (const child of children) {
      // Router state snapshots may not be populated yet when this service is
      // constructed mid-navigation. Bail out safely — the NavigationEnd
      // subscription rebuilds the breadcrumbs once navigation completes.
      if (!child.snapshot) {
        return;
      }

      const segment = child.snapshot.url.map(s => s.path).join('/');

      if (!segment) {
        this.build(child, url, breadcrumbs);
        return;
      }

      const nextUrl = `${url}/${segment}`;
      const parentLabel = child.snapshot.data['parentBreadcrumb'];
      const parentRouterLink = child.snapshot.data['parentBreadcrumbLink'];
      const label = child.snapshot.data['breadcrumb'];

      if (
        parentLabel &&
        breadcrumbs[breadcrumbs.length - 1]?.label !== parentLabel
      ) {
        breadcrumbs.push({
          label: parentLabel,
          routerLink: parentRouterLink || '/tenant'
        });
      }

      if (
        label &&
        breadcrumbs[breadcrumbs.length - 1]?.label !== label
      ) {
        breadcrumbs.push({
          label,
          routerLink: nextUrl
        });
      }

      this.build(child, nextUrl, breadcrumbs);
      return;
    }
  }
}
